// Solid pictures (14 Oct 2026). A `solid_3d` is one named solid drawn on a
// slant, with its hidden edges dashed; a cube or a rectangular prism may
// carry a label on its length, width and height. A `net` is the flat
// pattern of faces that folds into a named solid.
//
// A prism or a pyramid may also be given by the number of sides of its
// base (`"solid": "prism", "sides": 9`). No solid is drawn to scale: every
// rectangular prism is the same drawing whatever its labels say. An edge
// marked "?" carries no length anywhere: it is drawn, and never written or
// spoken.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

type SolidForm =
  | { kind: 'cuboid'; cube: boolean }
  | { kind: 'prism' | 'pyramid'; sides: number }
  | { kind: 'cylinder' | 'cone' | 'sphere' };

const NAMED_SOLIDS = {
  cube: { kind: 'cuboid', cube: true },
  rectangular_prism: { kind: 'cuboid', cube: false },
  triangular_prism: { kind: 'prism', sides: 3 },
  pentagonal_prism: { kind: 'prism', sides: 5 },
  hexagonal_prism: { kind: 'prism', sides: 6 },
  octagonal_prism: { kind: 'prism', sides: 8 },
  triangular_pyramid: { kind: 'pyramid', sides: 3 },
  square_pyramid: { kind: 'pyramid', sides: 4 },
  pentagonal_pyramid: { kind: 'pyramid', sides: 5 },
  hexagonal_pyramid: { kind: 'pyramid', sides: 6 },
  cylinder: { kind: 'cylinder' },
  cone: { kind: 'cone' },
  sphere: { kind: 'sphere' },
} as const satisfies Record<string, SolidForm>;

type NamedSolid = keyof typeof NAMED_SOLIDS;

const SOLID_NAMES = Object.keys(NAMED_SOLIDS) as NamedSolid[];

export type SolidName = NamedSolid | 'prism' | 'pyramid';

// A length ("6 cm"), a letter, "?" for the edge to find, or "" for a bare
// edge.
export type SolidLabels = { length: string; width: string; height: string };

export type Solid3dSpec = {
  type: 'solid_3d';
  solid: SolidName;
  // The sides of the base, for a `prism` or a `pyramid` given by number.
  sides: number | null;
  labels: SolidLabels;
};

export type NetSpec = {
  type: 'net';
  // A sphere has no net.
  solid: Exclude<SolidName, 'sphere'>;
  sides: number | null;
};

// A flat pattern laid out on a grid of squares (Year 7, 1 Nov 2026): any
// net of squares, with triangles standing on the outside of some of them.
// It draws correct nets in any layout and nets that will not fold, in the
// same colours and lines, and is described the same way for both.
export type NetGridSide = 'top' | 'right' | 'bottom' | 'left';
export type NetGridSpec = {
  type: 'net_grid';
  // [column, row], counted from the top left, from 0.
  squares: [number, number][];
  // A triangle with equal sides stands on one side of a square.
  triangles: { on: [number, number]; side: NetGridSide }[];
};

// A base with more sides than this is not a fair picture.
const MAX_BASE_SIDES = 10;

const LENGTH = /^\d+(?:\.\d+)?(?: ?(?:mm|cm|m|km))?$/;
const LETTER = /^[a-zA-Z]$/;

const EDGES = ['length', 'width', 'height'] as const;

function readSolid(
  record: Record<string, unknown>,
): { solid: SolidName; sides: number | null } | null {
  const { solid, sides } = record;
  if (solid === 'prism' || solid === 'pyramid') {
    if (
      typeof sides !== 'number' ||
      !Number.isInteger(sides) ||
      sides < 3 ||
      sides > MAX_BASE_SIDES
    )
      return null;
    return { solid, sides };
  }
  const name = SOLID_NAMES.find((known) => known === solid);
  // A named solid already says how many sides its base has.
  if (!name || sides !== undefined) return null;
  return { solid: name, sides: null };
}

function formOf(solid: SolidName, sides: number | null): SolidForm {
  if (solid === 'prism' || solid === 'pyramid')
    return { kind: solid, sides: sides ?? 3 };
  return NAMED_SOLIDS[solid];
}

function readSolidLabels(value: unknown, form: SolidForm): SolidLabels | null {
  const labels: SolidLabels = { length: '', width: '', height: '' };
  if (value === undefined) return labels;
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return null;
  for (const [key, entry] of Object.entries(value)) {
    const edge = EDGES.find((known) => known === key);
    if (!edge || typeof entry !== 'string') return null;
    const text = entry.trim();
    if (text !== '' && text !== '?' && !LETTER.test(text) && !LENGTH.test(text))
      return null;
    labels[edge] = text;
  }
  const written = EDGES.map((edge) => labels[edge]).filter(
    (text) => text !== '',
  );
  // Only a box has a length, a width and a height to label.
  if (form.kind !== 'cuboid') return written.length === 0 ? labels : null;
  // Every edge of a cube is the same, so its labels cannot disagree.
  const known = written.filter((text) => text !== '?');
  if (form.cube && known.some((text) => text !== known[0])) return null;
  return labels;
}

export function readSolid3d(
  record: Record<string, unknown>,
): Solid3dSpec | null {
  const named = readSolid(record);
  if (!named) return null;
  const labels = readSolidLabels(
    record.labels,
    formOf(named.solid, named.sides),
  );
  if (!labels) return null;
  return { type: 'solid_3d', ...named, labels };
}

export function readNet(record: Record<string, unknown>): NetSpec | null {
  const named = readSolid(record);
  if (!named || named.solid === 'sphere') return null;
  return { type: 'net', solid: named.solid, sides: named.sides };
}

// More faces than this are not a fair picture.
const MAX_GRID_FACES = 10;
const GRID_SIDES: NetGridSide[] = ['top', 'right', 'bottom', 'left'];
const STEP: Record<NetGridSide, [number, number]> = {
  top: [0, -1],
  right: [1, 0],
  bottom: [0, 1],
  left: [-1, 0],
};

function readCell(value: unknown): [number, number] | null {
  if (!Array.isArray(value) || value.length !== 2) return null;
  const [column, row] = value as unknown[];
  if (
    typeof column !== 'number' ||
    typeof row !== 'number' ||
    !Number.isInteger(column) ||
    !Number.isInteger(row) ||
    column < 0 ||
    row < 0 ||
    column > 9 ||
    row > 9
  )
    return null;
  return [column, row];
}

const cellKey = ([column, row]: [number, number]) =>
  `${String(column)},${String(row)}`;

export function readNetGrid(
  record: Record<string, unknown>,
): NetGridSpec | null {
  if (!Array.isArray(record.squares) || record.squares.length < 1) return null;
  const squares: [number, number][] = [];
  const taken = new Set<string>();
  for (const entry of record.squares as unknown[]) {
    const cell = readCell(entry);
    if (!cell || taken.has(cellKey(cell))) return null;
    taken.add(cellKey(cell));
    squares.push(cell);
  }
  // The squares join edge to edge into one piece.
  const reached = new Set([cellKey(squares[0] ?? [0, 0])]);
  const queue = [squares[0] ?? [0, 0]];
  for (const [column, row] of queue)
    for (const [dx, dy] of Object.values(STEP)) {
      const next: [number, number] = [column + dx, row + dy];
      if (taken.has(cellKey(next)) && !reached.has(cellKey(next))) {
        reached.add(cellKey(next));
        queue.push(next);
      }
    }
  if (reached.size !== squares.length) return null;
  const triangles: NetGridSpec['triangles'] = [];
  // A triangle reaches into the cell beside its square, which must be empty
  // and reached by no other triangle.
  const used = new Set<string>();
  const given = record.triangles === undefined ? [] : record.triangles;
  if (!Array.isArray(given)) return null;
  for (const entry of given as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const { on, side } = entry as Record<string, unknown>;
    const cell = readCell(on);
    const way = GRID_SIDES.find((known) => known === side);
    if (!cell || !way || !taken.has(cellKey(cell))) return null;
    const [dx, dy] = STEP[way];
    const beside = `${String(cell[0] + dx)},${String(cell[1] + dy)}`;
    if (taken.has(beside) || used.has(beside)) return null;
    used.add(beside);
    triangles.push({ on: cell, side: way });
  }
  if (squares.length + triangles.length > MAX_GRID_FACES) return null;
  return { type: 'net_grid', squares, triangles };
}

function spokenEdge(text: string): string {
  if (text === '?') return 'unknown and is marked with a question mark';
  return `labelled ${LETTER.test(text) ? `the letter ${text}` : text}`;
}

function spokenJoin(parts: string[]): string {
  if (parts.length < 2) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`;
}

function solidWords(solid: SolidName, sides: number | null): string {
  if (solid === 'prism')
    return `a prism whose two ends each have ${String(sides)} sides`;
  if (solid === 'pyramid')
    return `a pyramid whose base has ${String(sides)} sides`;
  const name = solid.replace('_', ' ');
  return `${name.startsWith('o') ? 'an' : 'a'} ${name}`;
}

// What a screen reader is told in place of the solid: its name, as every
// question that shows one already calls it "this prism" or "this pyramid",
// and the labels that are written. Never a count of its faces, edges or
// corners, which is what those questions ask for.
export function describeSolid3d(solid: Solid3dSpec): string {
  const form = formOf(solid.solid, solid.sides);
  const name = solidWords(solid.solid, solid.sides);
  const curved =
    form.kind === 'cylinder' || form.kind === 'cone' || form.kind === 'sphere';
  const parts = [
    curved
      ? `A drawing of ${name}.`
      : `A drawing of ${name}, with its hidden edges shown as dashed lines.`,
  ];
  const labelled = EDGES.filter((edge) => solid.labels[edge] !== '').map(
    (edge) => `its ${edge} is ${spokenEdge(solid.labels[edge])}`,
  );
  if (labelled.length > 0) {
    const words = spokenJoin(labelled);
    parts.push(`${words.charAt(0).toUpperCase()}${words.slice(1)}.`);
  }
  return parts.join(' ');
}

// A face in plain words. A face with five sides or more is never named, as
// on a flat shape (practice-shape-data.ts).
function faceWords(sides: number): string {
  if (sides === 3) return 'triangle';
  if (sides === 4) return 'square';
  return `shape with ${String(sides)} sides`;
}

// What a screen reader is told in place of the net: its faces and how they
// are joined. Never the solid it folds into.
export function describeNet(net: NetSpec): string {
  const form = formOf(net.solid, net.sides);
  switch (form.kind) {
    case 'cuboid':
      return `A flat pattern of 6 ${form.cube ? 'squares' : 'rectangles'} joined edge to edge: 4 in a row with 1 above and 1 below the second.`;
    case 'prism': {
      const end = faceWords(form.sides);
      return `A flat pattern of ${String(form.sides + 2)} shapes joined edge to edge: ${String(form.sides)} rectangles in a row, with 1 ${end} above and 1 ${end} below the second rectangle.`;
    }
    case 'pyramid':
      if (form.sides === 3)
        return 'A flat pattern of 4 triangles joined edge to edge: 1 in the middle with 1 on each of its 3 sides.';
      return `A flat pattern of ${String(form.sides + 1)} shapes joined edge to edge: 1 ${faceWords(form.sides)} in the middle, with 1 triangle on each of its ${String(form.sides)} sides.`;
    case 'cylinder':
      return 'A flat pattern of 3 shapes: 1 rectangle, with 1 circle touching its top side and 1 circle touching its bottom side.';
    case 'cone':
      return 'A flat pattern of 2 shapes: 1 wedge cut from a circle, like a slice of pie, with 1 small circle touching its curved side.';
    case 'sphere':
      return '';
  }
}

const counted = (count: number, one: string) =>
  `${String(count)} ${one}${count === 1 ? '' : 's'}`;

function columnWords(columns: number[]): string {
  const words = columns.map(String);
  return `${columns.length === 1 ? 'column' : 'columns'} ${spokenJoin(words)}`;
}

// The same words for a net that folds and one that does not: its faces,
// row by row, and where each triangle stands. Rows are counted from the
// top and columns from the left of the pattern.
export function describeNetGrid(net: NetGridSpec): string {
  const left = Math.min(...net.squares.map(([column]) => column));
  const top = Math.min(...net.squares.map(([, row]) => row));
  const rows = new Map<number, number[]>();
  for (const [column, row] of net.squares)
    rows.set(row - top, [...(rows.get(row - top) ?? []), column - left + 1]);
  const order = [...rows.keys()].sort((a, b) => a - b);
  const faces =
    net.triangles.length === 0
      ? counted(net.squares.length, 'square')
      : `${counted(net.squares.length, 'square')} and ${counted(net.triangles.length, 'triangle')}`;
  const parts = [
    `A flat pattern of ${faces} joined edge to edge, in ${counted(order.length, 'row')} of squares. Rows are counted from the top and columns from the left.`,
  ];
  // The squares are joined, so no row between two others is empty.
  for (const row of order) {
    const columns = (rows.get(row) ?? []).sort((a, b) => a - b);
    parts.push(
      `Row ${String(row + 1)}: ${counted(columns.length, 'square')}, in ${columnWords(columns)}.`,
    );
  }
  for (const triangle of net.triangles)
    parts.push(
      `A triangle stands on the ${triangle.side} side of the square in row ${String(triangle.on[1] - top + 1)}, column ${String(triangle.on[0] - left + 1)}.`,
    );
  return parts.join(' ');
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type SolidPoint = { x: number; y: number };
export type SolidLine = { from: SolidPoint; to: SolidPoint };
export type SolidBox = { x: number; y: number; width: number; height: number };

export const SOLID_LABEL_SIZE = 14;
const LETTER_WIDTH = 8;
const PAD = 6;

const fixed = (value: number) => value.toFixed(2);
const at = (point: SolidPoint) => `${fixed(point.x)} ${fixed(point.y)}`;

function closedPath(points: SolidPoint[]): string {
  return `${points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${at(point)}`)
    .join(' ')} Z`;
}

function circlePath(centre: SolidPoint, radius: number): string {
  const r = fixed(radius);
  return `M ${at({ x: centre.x - radius, y: centre.y })} A ${r} ${r} 0 1 0 ${at({ x: centre.x + radius, y: centre.y })} A ${r} ${r} 0 1 0 ${at({ x: centre.x - radius, y: centre.y })} Z`;
}

function boxOf(reach: SolidPoint[]): SolidBox {
  const xs = reach.map((point) => point.x);
  const ys = reach.map((point) => point.y);
  const left = Math.min(...xs) - PAD;
  const top = Math.min(...ys) - PAD;
  return {
    x: left,
    y: top,
    width: Math.max(...xs) + PAD - left,
    height: Math.max(...ys) + PAD - top,
  };
}

function rectangle(
  x: number,
  y: number,
  width: number,
  height: number,
): SolidPoint[] {
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
}

function middleOf(points: SolidPoint[]): SolidPoint {
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

// A shape with equal sides and equal corners standing on the side from
// `from` to `to`, on the side of it that faces away from `inside`.
function regularOn(
  from: SolidPoint,
  to: SolidPoint,
  count: number,
  inside: SolidPoint,
): SolidPoint[] {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  const build = (turn: number) => {
    const points = [from, to];
    let heading = Math.atan2(to.y - from.y, to.x - from.x);
    let last = to;
    for (let index = 2; index < count; index += 1) {
      heading += (turn * 2 * Math.PI) / count;
      last = {
        x: last.x + length * Math.cos(heading),
        y: last.y + length * Math.sin(heading),
      };
      points.push(last);
    }
    return points;
  };
  const far = (points: SolidPoint[]) => {
    const middle = middleOf(points);
    return Math.hypot(middle.x - inside.x, middle.y - inside.y);
  };
  const one = build(1);
  const other = build(-1);
  return far(one) > far(other) ? one : other;
}

export type NetFace = {
  shape: 'polygon' | 'circle' | 'sector';
  // The corners of a face with straight sides; none for a curved face.
  points: SolidPoint[];
  d: string;
};

export type NetPicture = {
  faces: NetFace[];
  // The sides round the outside of the pattern, drawn solid.
  cuts: SolidLine[];
  // The sides two faces share, where the pattern folds, drawn dashed.
  folds: SolidLine[];
  box: SolidBox;
};

const CUBE_SIDE = 44;
// The length, width and height of the rectangular prism a net folds into:
// no two alike, so no face is a square.
const BOX_NET = { length: 66, width: 40, height: 28 };
const NET_WIDTH = 260;
const PRISM_SIDE = 44;
const PYRAMID_SIDE = 64;
const PYRAMID_REACH = 220;

function cuboidNet(cube: boolean): SolidPoint[][] {
  const { length, width, height } = cube
    ? { length: CUBE_SIDE, width: CUBE_SIDE, height: CUBE_SIDE }
    : BOX_NET;
  const faces: SolidPoint[][] = [];
  let x = 0;
  for (const across of [width, length, width, length]) {
    faces.push(rectangle(x, 0, across, height));
    x += across;
  }
  faces.push(rectangle(width, -width, length, width));
  faces.push(rectangle(width, height, length, width));
  return faces;
}

function prismNet(sides: number): SolidPoint[][] {
  const side = Math.min(PRISM_SIDE, NET_WIDTH / sides);
  const height = side * 1.6;
  const faces: SolidPoint[][] = [];
  for (let index = 0; index < sides; index += 1)
    faces.push(rectangle(index * side, 0, side, height));
  const inside = { x: side * 1.5, y: height / 2 };
  faces.push(
    regularOn({ x: side, y: 0 }, { x: side * 2, y: 0 }, sides, inside),
  );
  faces.push(
    regularOn(
      { x: side, y: height },
      { x: side * 2, y: height },
      sides,
      inside,
    ),
  );
  return faces;
}

function pyramidNet(sides: number): SolidPoint[][] {
  // From the middle of the base to the middle of a side, and the height of
  // each triangle, for a side of 1. A triangle must be taller than that
  // reach or the points would not meet when the net is folded.
  const reach = 1 / (2 * Math.tan(Math.PI / sides));
  const tall = Math.max(Math.sqrt(3) / 2, reach * 1.3);
  const side = Math.min(PYRAMID_SIDE, PYRAMID_REACH / (2 * (reach + tall)));
  const radius = side / (2 * Math.sin(Math.PI / sides));
  const base: SolidPoint[] = [];
  // The bottom side lies flat.
  for (let index = 0; index < sides; index += 1) {
    const angle = Math.PI / 2 + Math.PI / sides + (index * 2 * Math.PI) / sides;
    base.push({ x: radius * Math.cos(angle), y: radius * Math.sin(angle) });
  }
  const faces = [base];
  for (const [index, from] of base.entries()) {
    const to = base[(index + 1) % sides] ?? from;
    const middle = middleOf([from, to]);
    const out = Math.hypot(middle.x, middle.y);
    faces.push([
      from,
      to,
      {
        x: middle.x + (middle.x / out) * tall * side,
        y: middle.y + (middle.y / out) * tall * side,
      },
    ]);
  }
  return faces;
}

const CYLINDER_NET = { radius: 24, height: 64 };
const CONE_NET = { radius: 26, slant: 96 };

function curvedNet(kind: 'cylinder' | 'cone'): NetPicture {
  if (kind === 'cylinder') {
    const { radius, height } = CYLINDER_NET;
    // The rectangle wraps once round each circle.
    const width = 2 * Math.PI * radius;
    const side = rectangle(0, 0, width, height);
    const top = { x: width / 2, y: -radius };
    const bottom = { x: width / 2, y: height + radius };
    return {
      faces: [
        { shape: 'polygon', points: side, d: closedPath(side) },
        { shape: 'circle', points: [], d: circlePath(top, radius) },
        { shape: 'circle', points: [], d: circlePath(bottom, radius) },
      ],
      cuts: side.map((from, index) => ({
        from,
        to: side[(index + 1) % side.length] ?? from,
      })),
      folds: [],
      box: boxOf([
        ...side,
        { x: top.x, y: top.y - radius },
        { x: bottom.x, y: bottom.y + radius },
      ]),
    };
  }
  const { radius, slant } = CONE_NET;
  // The curved side of the wedge is as long as the way round the circle.
  const half = (Math.PI * radius) / slant;
  const left = { x: -slant * Math.sin(half), y: slant * Math.cos(half) };
  const right = { x: slant * Math.sin(half), y: slant * Math.cos(half) };
  const circle = { x: 0, y: slant + radius };
  return {
    faces: [
      {
        shape: 'sector',
        points: [],
        d: `M 0.00 0.00 L ${at(left)} A ${fixed(slant)} ${fixed(slant)} 0 0 0 ${at(right)} Z`,
      },
      { shape: 'circle', points: [], d: circlePath(circle, radius) },
    ],
    cuts: [],
    folds: [],
    box: boxOf([{ x: 0, y: 0 }, left, right, { x: 0, y: circle.y + radius }]),
  };
}

function gridNet(net: NetGridSpec): SolidPoint[][] {
  const side = CUBE_SIDE;
  const tall = (side * Math.sqrt(3)) / 2;
  const faces = net.squares.map(([column, row]) =>
    rectangle(column * side, row * side, side, side),
  );
  for (const { on, side: which } of net.triangles) {
    const [x, y] = [on[0] * side, on[1] * side];
    const corners: Record<NetGridSide, SolidPoint[]> = {
      top: [
        { x, y },
        { x: x + side / 2, y: y - tall },
        { x: x + side, y },
      ],
      right: [
        { x: x + side, y },
        { x: x + side + tall, y: y + side / 2 },
        { x: x + side, y: y + side },
      ],
      bottom: [
        { x: x + side, y: y + side },
        { x: x + side / 2, y: y + side + tall },
        { x, y: y + side },
      ],
      left: [
        { x, y: y + side },
        { x: x - tall, y: y + side / 2 },
        { x, y },
      ],
    };
    faces.push(corners[which]);
  }
  return faces;
}

export function netPicture(net: NetSpec | NetGridSpec): NetPicture {
  if (net.type === 'net_grid') return foldedLines(gridNet(net));
  const form = formOf(net.solid, net.sides);
  if (form.kind === 'cylinder' || form.kind === 'cone')
    return curvedNet(form.kind);
  const corners =
    form.kind === 'pyramid'
      ? pyramidNet(form.sides)
      : form.kind === 'prism'
        ? prismNet(form.sides)
        : cuboidNet(form.kind === 'cuboid' && form.cube);
  return foldedLines(corners);
}

function foldedLines(corners: SolidPoint[][]): NetPicture {
  // A side that belongs to two faces is a fold; any other is cut.
  const sides = new Map<string, { line: SolidLine; faces: number }>();
  for (const face of corners) {
    for (const [index, from] of face.entries()) {
      const to = face[(index + 1) % face.length] ?? from;
      const key = [at(from), at(to)].sort().join(' to ');
      const known = sides.get(key);
      if (known) known.faces += 1;
      else sides.set(key, { line: { from, to }, faces: 1 });
    }
  }
  const lines = [...sides.values()];
  return {
    faces: corners.map((points) => ({
      shape: 'polygon',
      points,
      d: closedPath(points),
    })),
    cuts: lines.filter((side) => side.faces === 1).map((side) => side.line),
    folds: lines.filter((side) => side.faces > 1).map((side) => side.line),
    box: boxOf(corners.flat()),
  };
}

export type SolidPictureLabel = SolidPoint & {
  text: string;
  edge: (typeof EDGES)[number];
};

export type SolidCurve = { d: string; hidden: boolean };

export type SolidPicture = {
  // The faces that can be seen, filled.
  faces: string[];
  // The corners of a solid with flat faces, in front or behind.
  corners: SolidPoint[];
  edges: SolidLine[];
  hidden: SolidLine[];
  curves: SolidCurve[];
  labels: SolidPictureLabel[];
  box: SolidBox;
};

type Point3 = { x: number; y: number; z: number };

// The solid is seen from the front and a little above. x runs across, y
// comes toward the viewer and z goes up.
const TILT = (30 * Math.PI) / 180;
const SQUASH = Math.sin(TILT);
const RISE = Math.cos(TILT);

function flat(point: Point3): SolidPoint {
  return { x: point.x, y: SQUASH * point.y - RISE * point.z };
}

// A solid with flat faces. An edge is hidden when no face it belongs to
// can be seen.
function facedPicture(corners: Point3[], faces: number[][]): SolidPicture {
  const middle = {
    x: corners.reduce((sum, corner) => sum + corner.x, 0) / corners.length,
    y: corners.reduce((sum, corner) => sum + corner.y, 0) / corners.length,
    z: corners.reduce((sum, corner) => sum + corner.z, 0) / corners.length,
  };
  const origin = { x: 0, y: 0, z: 0 };
  const seen = faces.map((face) => {
    const a = corners[face[0] ?? 0] ?? origin;
    const b = corners[face[1] ?? 0] ?? origin;
    const c = corners[face[2] ?? 0] ?? origin;
    const u = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
    const v = { x: c.x - a.x, y: c.y - a.y, z: c.z - a.z };
    const normal = {
      x: u.y * v.z - u.z * v.y,
      y: u.z * v.x - u.x * v.z,
      z: u.x * v.y - u.y * v.x,
    };
    // Which way is out: away from the middle of the solid.
    const out =
      normal.x * (a.x - middle.x) +
        normal.y * (a.y - middle.y) +
        normal.z * (a.z - middle.z) >
      0
        ? 1
        : -1;
    return out * (normal.y * RISE + normal.z * SQUASH) > 1e-9;
  });
  const points = corners.map(flat);
  const sides = new Map<string, { line: SolidLine; seen: boolean }>();
  for (const [index, face] of faces.entries()) {
    for (const [place, from] of face.entries()) {
      const to = face[(place + 1) % face.length] ?? from;
      const key = `${String(Math.min(from, to))} ${String(Math.max(from, to))}`;
      const known = sides.get(key);
      const visible = seen[index] ?? false;
      if (known) known.seen = known.seen || visible;
      else
        sides.set(key, {
          line: {
            from: points[from] ?? { x: 0, y: 0 },
            to: points[to] ?? { x: 0, y: 0 },
          },
          seen: visible,
        });
    }
  }
  const lines = [...sides.values()];
  return {
    faces: faces
      .filter((_face, index) => seen[index])
      .map((face) =>
        closedPath(face.map((corner) => points[corner] ?? { x: 0, y: 0 })),
      ),
    corners: points,
    edges: lines.filter((side) => side.seen).map((side) => side.line),
    hidden: lines.filter((side) => !side.seen).map((side) => side.line),
    curves: [],
    labels: [],
    box: boxOf(points),
  };
}

const PRISM = { radius: 62, height: 120 };
const PYRAMID = { radius: 78, height: 115 };

// The corners of the base. `turn` is how far round the first corner sits,
// as a part of the way from one corner to the next. A quarter keeps every
// corner of a prism clear of the others, so no upright edge hides another.
function baseCorners(
  sides: number,
  radius: number,
  z: number,
  turn = 0.25,
): Point3[] {
  const corners: Point3[] = [];
  for (let index = 0; index < sides; index += 1) {
    const angle = ((turn + index) * 2 * Math.PI) / sides;
    corners.push({
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
      z,
    });
  }
  return corners;
}

function prismPicture(sides: number): SolidPicture {
  const count = Array.from({ length: sides }, (_, index) => index);
  return facedPicture(
    [
      ...baseCorners(sides, PRISM.radius, 0),
      ...baseCorners(sides, PRISM.radius, PRISM.height),
    ],
    [
      count,
      count.map((index) => index + sides),
      ...count.map((index) => {
        const next = (index + 1) % sides;
        return [index, next, next + sides, index + sides];
      }),
    ],
  );
}

// The sloping edges of a pyramid all meet at its top point, where an edge
// behind can sit almost on an edge in front. The base is turned to the
// place that leaves the widest gap between the two closest of them. A
// triangular pyramid has room to spare, and keeps one face to the front.
function pyramidTurn(sides: number): number {
  if (sides === 3) return 0.25;
  const top = flat({ x: 0, y: 0, z: PYRAMID.height });
  let best = 0.25;
  let widest = 0;
  for (let step = 1; step < 100; step += 1) {
    const slopes = baseCorners(sides, PYRAMID.radius, 0, step / 100)
      .map(flat)
      .map((corner) => Math.atan2(corner.y - top.y, corner.x - top.x))
      .sort((a, b) => a - b);
    const narrowest = Math.min(
      ...slopes.slice(1).map((slope, index) => slope - (slopes[index] ?? 0)),
    );
    if (narrowest > widest + 1e-9) {
      widest = narrowest;
      best = step / 100;
    }
  }
  return best;
}

function pyramidPicture(sides: number): SolidPicture {
  const count = Array.from({ length: sides }, (_, index) => index);
  return facedPicture(
    [
      ...baseCorners(sides, PYRAMID.radius, 0, pyramidTurn(sides)),
      { x: 0, y: 0, z: PYRAMID.height },
    ],
    [count, ...count.map((index) => [index, (index + 1) % sides, sides])],
  );
}

const CUBE = { width: 96, height: 96, back: { x: 40, y: 32 } };
const BOX = { width: 150, height: 80, back: { x: 52, y: 30 } };
const LABEL_GAP = 9;

// A box drawn the way a school book draws it: the front face square on,
// the faces behind it pushed up and to the right.
function cuboidPicture(cube: boolean, labels: SolidLabels): SolidPicture {
  const { width, height, back } = cube ? CUBE : BOX;
  const front = rectangle(0, 0, width, height);
  const behind = front.map((corner) => ({
    x: corner.x + back.x,
    y: corner.y - back.y,
  }));
  const [topLeft, topRight, bottomRight, bottomLeft] = front;
  const [backTopLeft, backTopRight, backBottomRight, backBottomLeft] = behind;
  if (
    !topLeft ||
    !topRight ||
    !bottomRight ||
    !bottomLeft ||
    !backTopLeft ||
    !backTopRight ||
    !backBottomRight ||
    !backBottomLeft
  )
    throw new Error('a box has eight corners');
  const line = (from: SolidPoint, to: SolidPoint) => ({ from, to });
  const room = (text: string) => (text.length * LETTER_WIDTH) / 2;
  // Each label sits outside the box, beside an edge that can be seen.
  const places: SolidPictureLabel[] = [
    {
      edge: 'length',
      text: labels.length,
      x: width / 2,
      y: height + LABEL_GAP + 5,
    },
    {
      edge: 'width',
      text: labels.width,
      x: width + back.x / 2 + LABEL_GAP + room(labels.width),
      y: height - back.y / 2 + LABEL_GAP,
    },
    {
      edge: 'height',
      text: labels.height,
      x: width + back.x + LABEL_GAP + room(labels.height),
      y: height / 2 - back.y,
    },
  ];
  const shown = places.filter((label) => label.text !== '');
  return {
    faces: [
      closedPath(front),
      closedPath([topLeft, backTopLeft, backTopRight, topRight]),
      closedPath([topRight, backTopRight, backBottomRight, bottomRight]),
    ],
    corners: [...front, ...behind],
    edges: [
      line(topLeft, topRight),
      line(topRight, bottomRight),
      line(bottomRight, bottomLeft),
      line(bottomLeft, topLeft),
      line(topLeft, backTopLeft),
      line(topRight, backTopRight),
      line(bottomRight, backBottomRight),
      line(backTopLeft, backTopRight),
      line(backTopRight, backBottomRight),
    ],
    hidden: [
      line(bottomLeft, backBottomLeft),
      line(backBottomLeft, backTopLeft),
      line(backBottomLeft, backBottomRight),
    ],
    curves: [],
    labels: shown,
    box: boxOf([
      ...front,
      ...behind,
      ...shown.flatMap((label) => [
        { x: label.x - room(label.text), y: label.y - 9 },
        { x: label.x + room(label.text), y: label.y + 9 },
      ]),
    ]),
  };
}

const ROUND = { across: 62, height: 118 };
const CONE = { across: 66, height: 128 };
const SPHERE = 68;

// Half of an oval from its left end to its right end: the near half, drawn
// solid, or the far half, drawn dashed.
function halfOval(
  from: SolidPoint,
  to: SolidPoint,
  across: number,
  deep: number,
  near: boolean,
  wide = false,
): string {
  return `M ${at(from)} A ${fixed(across)} ${fixed(deep)} 0 ${wide ? '1' : '0'} ${near ? '0' : '1'} ${at(to)}`;
}

function curvedPicture(kind: 'cylinder' | 'cone' | 'sphere'): SolidPicture {
  const none = { corners: [], edges: [], hidden: [], labels: [] };
  if (kind === 'sphere') {
    const left = { x: -SPHERE, y: 0 };
    const right = { x: SPHERE, y: 0 };
    const deep = SPHERE * SQUASH;
    const outline = circlePath({ x: 0, y: 0 }, SPHERE);
    return {
      ...none,
      faces: [outline],
      curves: [
        { d: outline, hidden: false },
        { d: halfOval(left, right, SPHERE, deep, true), hidden: false },
        { d: halfOval(left, right, SPHERE, deep, false), hidden: true },
      ],
      box: boxOf([
        { x: -SPHERE, y: -SPHERE },
        { x: SPHERE, y: SPHERE },
      ]),
    };
  }
  if (kind === 'cylinder') {
    const { across, height } = ROUND;
    const deep = across * SQUASH;
    const left = { x: -across, y: 0 };
    const right = { x: across, y: 0 };
    const topLeft = { x: -across, y: -height };
    const topRight = { x: across, y: -height };
    const near = halfOval(left, right, across, deep, true);
    const top = `${halfOval(topLeft, topRight, across, deep, true)} A ${fixed(across)} ${fixed(deep)} 0 0 0 ${at(topLeft)} Z`;
    return {
      ...none,
      faces: [
        `${near} L ${at(topRight)} A ${fixed(across)} ${fixed(deep)} 0 0 0 ${at(topLeft)} Z`,
      ],
      curves: [
        { d: top, hidden: false },
        { d: near, hidden: false },
        { d: `M ${at(left)} L ${at(topLeft)}`, hidden: false },
        { d: `M ${at(right)} L ${at(topRight)}`, hidden: false },
        { d: halfOval(left, right, across, deep, false), hidden: true },
      ],
      box: boxOf([
        { x: -across, y: -height - deep },
        { x: across, y: deep },
      ]),
    };
  }
  const { across, height } = CONE;
  const deep = across * SQUASH;
  const tip = { x: 0, y: -height };
  // Where the two sloping sides touch the oval base: a little behind its
  // widest points.
  const y = -(deep * deep) / height;
  const x = across * Math.sqrt(1 - (deep * deep) / (height * height));
  const left = { x: -x, y };
  const right = { x, y };
  const near = halfOval(left, right, across, deep, true, true);
  return {
    ...none,
    faces: [`${near} L ${at(tip)} Z`],
    curves: [
      { d: near, hidden: false },
      { d: `M ${at(left)} L ${at(tip)} L ${at(right)}`, hidden: false },
      { d: halfOval(left, right, across, deep, false), hidden: true },
    ],
    box: boxOf([
      { x: -across, y: -height },
      { x: across, y: deep },
    ]),
  };
}

export function solidPicture(solid: Solid3dSpec): SolidPicture {
  const form = formOf(solid.solid, solid.sides);
  switch (form.kind) {
    case 'cuboid':
      return cuboidPicture(form.cube, solid.labels);
    case 'prism':
      return prismPicture(form.sides);
    case 'pyramid':
      return pyramidPicture(form.sides);
    default:
      return curvedPicture(form.kind);
  }
}
